import React, { lazy, Suspense } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import ProtectedRoute from "../../components/auth/ProtectedRoute";
import ErrorBoundary from "../../features/shared/pages/ErrorBoundary";
import UnderDevelopment from "../../features/shared/pages/UnderDevelopment";
import PageLoader from "../../components/common/PageLoader";
import { APP_ROUTES } from "../routePaths";
import { createProtectedIndexRoute, RedirectWithQuery, withSuspense } from "./helpers";
import { USER_ROLES } from "../../utils/userRoles";
// Lazy-loaded page components for code splitting
const SaleOrderPage = lazy(() => import("../../features/sale-order/pages/SaleOrderPage").then(m => ({ default: m.default })));
const SaleOrderView = lazy(() => import("../../features/sale-order/pages/SaleOrderView").then(m => ({ default: m.default })));
const SaleOrderCheckView = lazy(() => import("../../features/sale-order/pages/SaleOrderCheckView").then(m => ({ default: m.default })));
const RTIPage = lazy(() => import("../../features/rti/pages/RTIPage"));
const RTIViewPage = lazy(() => import("../../features/rti/pages/RTIViewPage"));
const EmployeeAssignmentsPage = lazy(() => import("../../features/rti/pages/EmployeeAssignmentsPage"));
const QuotationPage = lazy(() => import("../../features/Quotaiton/Quotation"));
const QuotationViewPage = lazy(() => import("../../features/Quotaiton/pages/QuotationViewPage"));
const SaleInvoicePage = lazy(() => import("../../features/sale-invoice/SaleInvoice"));
const SaleInvoiceView = lazy(() => import("../../features/sale-invoice/pages/SaleInvoiceView").then(m => ({ default: m.default })));
const ReceiptPage = lazy(() => import("../../features/receipt/pages/ReceiptPage"));
const ReceiptViewPage = lazy(() => import("../../features/receipt/pages/ReceiptViewPage"));
const SaleCreditPage = lazy(() => import("../../features/sale-credit/pages/SaleCreditPage"));
const SaleCreditViewPage = lazy(() => import("../../features/sale-credit/pages/SaleCreditViewPage"));
const AccountsReceivablePage = lazy(() => import("../../features/accounts-receivable/pages/AccountsReceivablePage"));
const BillsListPage = lazy(() => import("../../features/bills/pages/BillsListPage"));
const BillsFormPage = lazy(() => import("../../features/bills/pages/BillsFormPage"));
const TransportDashboardPage = lazy(() => import("../../features/transport-dashboard/pages/TransportDashboardPage").then(m => ({ default: m.default })));
const DriverCommentsPage = lazy(() => import("../../features/driver-comments/pages/DriverCommentsPage").then(m => ({ default: m.default })));
const TransportOngoingJobView = lazy(() => import("../../features/transport-ongoing-job/pages/TransportOngoingJobView").then(m => ({ default: m.default })));
const TruckMasterView = lazy(() => import("../../features/truck-master/pages/TruckMasterView").then(m => ({ default: m.default })));
const SaleJobViewPage = lazy(() => import("../../features/sale-order/pages/SaleJobViewPage").then(m => ({ default: m.default })));
const BoardingJobViewPage = lazy(() => import("../../features/boarding-job/pages/BoardingJobViewPage").then(m => ({ default: m.default })));
const JobOrderPage = lazy(() => import("../../features/job-order/pages/JobOrderPage"));
const JobOrderView = lazy(() => import("../../features/job-order/JobOrderView"));
const ZbEntryFormPage = lazy(() => import("../../features/zb-entry/pages/ZbEntryFormPage"));
const PlanningAiCheck = lazy(() => import("../../features/Planning/PlanningAiCheck"));

function LegacyPlanningEditRedirect() {
  const { id } = useParams();
  return <Navigate replace to={APP_ROUTES.planningEdit(id)} />;
}

function LegacySaleOrderRedirect() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("Id") ?? searchParams.get("id");
  const numericId = Number(id);

  if (Number.isInteger(numericId) && numericId > 0) {
    return <Navigate replace to={APP_ROUTES.saleOrderEdit(numericId)} />;
  }

  return <Navigate replace to={APP_ROUTES.saleOrder} />;
}

export const salesTransactionRoutes = [
  {
    path: APP_ROUTES.saleOrder,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><SaleOrderPage /></Suspense> }],
  },
  {
    path: APP_ROUTES.saleOrderView,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><SaleOrderView /></Suspense> }],
  },
  {
    path: APP_ROUTES.saleOrderCheck,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><SaleOrderCheckView /></Suspense> }],
  },
  {
    path: APP_ROUTES.saleOrderEdit(),
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><SaleOrderPage /></Suspense> }],
  },
  {
    path: APP_ROUTES.saleOrderDrafts,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <UnderDevelopment /> }],
  },
  {
    path: APP_ROUTES.bookingDraftForm(),
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <UnderDevelopment /> }],
  },
  {
    caseSensitive: true,
    path: APP_ROUTES.saleOrderLegacy,
    element: (
      <ProtectedRoute>
        <LegacySaleOrderRedirect />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    caseSensitive: true,
    path: APP_ROUTES.saleOrderCheckLegacy,
    element: <Navigate replace to={APP_ROUTES.saleOrderCheck} />,
  },
  {
    path: APP_ROUTES.quotation,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><QuotationPage /></Suspense> }],
  },
  {
    path: APP_ROUTES.quotationView,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><QuotationViewPage /></Suspense> }],
  },
  {
    path: "/Quotation",
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><QuotationPage /></Suspense> }],
  },
  {
    // The canonical path, and the one APP_ROUTES.saleInvoice points at. It was
    // missing while /sale-invoice/view and /sale-invoice/edit/:id existed, so
    // everything that navigated by the constant — the employee dashboard tile,
    // the Back button on the invoice view, the sale order's Push Invoice —
    // landed on Page Not Found.
    path: APP_ROUTES.saleInvoice,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><SaleInvoicePage /></Suspense> }],
  },
  {
    // Legacy casing, kept for bookmarks and for the old app's links.
    path: "/SaleInvoice",
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><SaleInvoicePage /></Suspense> }],
  },
  {
    path: APP_ROUTES.saleInvoiceView,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><SaleInvoiceView /></Suspense> }],
  },
  {
    // Push Invoice from the sale order: a new invoice pre-loaded with that job.
    path: APP_ROUTES.saleInvoicePush(),
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><SaleInvoicePage /></Suspense> }],
  },
  {
    path: APP_ROUTES.saleInvoiceEdit(),
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><SaleInvoicePage /></Suspense> }],
  },
  // Bills routes
  {
    path: APP_ROUTES.bills,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><BillsListPage /></Suspense> }],
  },
  {
    path: APP_ROUTES.billsView,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><BillsListPage /></Suspense> }],
  },
  {
    path: APP_ROUTES.billsNew,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><BillsFormPage /></Suspense> }],
  },
  {
    path: APP_ROUTES.billsEdit(),
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><BillsFormPage /></Suspense> }],
  },
  // Legacy BillMaster route - redirects to new Bills form. The query string
  // must survive the redirect: ?Push=1 is how the purchase-order screen hands
  // a PO over for conversion to a bill.
  {
    caseSensitive: true,
    path: '/BillMaster',
    element: (
      <ProtectedRoute>
        <RedirectWithQuery to={APP_ROUTES.billsNew} />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    caseSensitive: true,
    path: APP_ROUTES.planningLegacy,
    element: (
      <ProtectedRoute>
        <Navigate replace to={APP_ROUTES.planning} />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    caseSensitive: true,
    path: APP_ROUTES.planningLegacyView,
    element: (
      <ProtectedRoute>
        <Navigate replace to={APP_ROUTES.planningView} />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    caseSensitive: true,
    path: APP_ROUTES.planningLegacyEdit(),
    element: (
      <ProtectedRoute>
        <LegacyPlanningEditRedirect />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    caseSensitive: true,
    path: APP_ROUTES.vesselPlanningLegacy,
    element: (
      <ProtectedRoute>
        <Navigate replace to={APP_ROUTES.vesselPlanning} />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    caseSensitive: true,
    path: APP_ROUTES.rti,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><RTIPage /></Suspense> }],
  },
  {
    caseSensitive: true,
    path: APP_ROUTES.rtiView,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><RTIViewPage /></Suspense> }],
  },
  {
    caseSensitive: true,
    path: APP_ROUTES.rtiEdit(),
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><RTIPage /></Suspense> }],
  },
  {
    caseSensitive: true,
    path: APP_ROUTES.employeeAssignments,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><EmployeeAssignmentsPage /></Suspense> }],
  },
  {
    caseSensitive: true,
    path: APP_ROUTES.rtiLegacy,
    element: (
      <ProtectedRoute>
        <Navigate replace to={APP_ROUTES.rti} />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/EnquiryMaster",
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <UnderDevelopment /> }],
  },
  {
    path: "/EnquiryMaster/EnquiryMasterTR",
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <UnderDevelopment /> }],
  },
  // Transport Dashboard — TRANSPORTATION role (1000) only
  {
    caseSensitive: true,
    path: APP_ROUTES.transportDashboard,
    element: (
      <ProtectedRoute requiredRoles={[USER_ROLES.TRANSPORTATION, USER_ROLES.SUPERADMIN, USER_ROLES.ADMIN]}>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageLoader />}>
            <TransportDashboardPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: APP_ROUTES.transportOngoingJobView,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageLoader />}>
            <TransportOngoingJobView />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: APP_ROUTES.driverComments,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageLoader />}>
            <DriverCommentsPage />
          </Suspense>
        ),
      },
    ],
  },
  // Sale Job View Report
  {
    caseSensitive: true,
    path: APP_ROUTES.saleJobView,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageLoader />}>
            <SaleJobViewPage />
          </Suspense>
        ),
      },
    ],
  },
  // Boarding Job View Report
  {
    caseSensitive: true,
    path: APP_ROUTES.boardingJobView,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageLoader />}>
            <BoardingJobViewPage />
          </Suspense>
        ),
      },
    ],
  },
  // Job Order
  {
    path: APP_ROUTES.jobOrderNew,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><JobOrderPage /></Suspense> }],
  },
  {
    path: APP_ROUTES.jobOrderEdit,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><JobOrderPage /></Suspense> }],
  },
  {
    path: APP_ROUTES.jobOrderView,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <Suspense fallback={<PageLoader />}><JobOrderView /></Suspense> }],
  },
];

export const accountsTransactionRoutes = [
  // Customer receipts (Spring /api/receipts): the entry screen and its
  // RECEIPT ENTRY VIEW, which the legacy page opened as an F5 popup.
  createProtectedIndexRoute(APP_ROUTES.receipt, withSuspense(<ReceiptPage />)),
  createProtectedIndexRoute(APP_ROUTES.receiptView, withSuspense(<ReceiptViewPage />)),
  // Sale credit notes (Spring /api/sale-credits): the entry screen and its
  // SALECREDIT ENTRY VIEW, which the legacy page opened as an F5 popup.
  {
    path: "/SaleCredit",
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: withSuspense(<SaleCreditPage />) }],
  },
  {
    path: "/SaleCredit/View",
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: withSuspense(<SaleCreditViewPage />) }],
  },
  // Accounts receivable desk. The legacy path is kept alongside the clean one
  // because the .NET menu and existing bookmarks point at it.
  createProtectedIndexRoute(APP_ROUTES.accountsReceivable, withSuspense(<AccountsReceivablePage />)),
  createProtectedIndexRoute(APP_ROUTES.accountsReceivableLegacy, withSuspense(<AccountsReceivablePage />)),
];

export const otherTransactionRoutes = [
  // ZB entry — port / ZB / PTW charges. The canonical path plus the legacy one,
  // which the forwarding dashboard's "ZB ADD" button still points at.
  createProtectedIndexRoute(APP_ROUTES.zbEntry, withSuspense(<ZbEntryFormPage />)),
  createProtectedIndexRoute(APP_ROUTES.planningAiCheck, withSuspense(<PlanningAiCheck />)),
  createProtectedIndexRoute(APP_ROUTES.zbEntryLegacy, withSuspense(<ZbEntryFormPage />)),
  {
    path: "/EmployeeMaster/EmployeeMailData",
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <UnderDevelopment /> }],
  },
  {
    path: "/EmployeeMaster/GoogleReview",
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [{ index: true, element: <UnderDevelopment /> }],
  },
];
